import cardValidator from "card-validator";

const MAX_EXPIRATION_CALENDAR_YEAR = 2100;
const NON_DIGIT_RE = /[^\d]/;

function maxExpirationElapsedYears(): number {
  return MAX_EXPIRATION_CALENDAR_YEAR - new Date().getFullYear();
}

export interface PaymentMethodFieldError {
  path: string[];
  message: string;
}

interface CardExpirationFieldPaths {
  month: string[];
  year: string[];
}

const defaultAddCardExpirationPaths: CardExpirationFieldPaths = {
  month: ["cardExpirationMonth"],
  year: ["cardExpirationYear"],
};

export function isCardExpirationValidForPayment(
  month: number | string,
  year: number | string,
): boolean {
  return cardValidator.expirationDate(
    { month: String(month), year: String(year) },
    maxExpirationElapsedYears(),
  ).isValid;
}

export function validateFullNameMin2(
  value: string,
): PaymentMethodFieldError | null {
  const trimmed = value.trim();
  if (trimmed.length < 2) {
    return { path: ["fullName"], message: "Enter your full name." };
  }
  if (trimmed.length > 255) {
    return { path: ["fullName"], message: "Name is too long." };
  }
  if (!NON_DIGIT_RE.test(trimmed)) {
    return { path: ["fullName"], message: "Enter your full name." };
  }
  return null;
}

export function validateCardExpirationStringFields(
  monthStr: string,
  yearStr: string,
  paths: CardExpirationFieldPaths = defaultAddCardExpirationPaths,
): PaymentMethodFieldError | null {
  const elapsed = maxExpirationElapsedYears();
  if (
    cardValidator.expirationDate({ month: monthStr, year: yearStr }, elapsed)
      .isValid
  ) {
    return null;
  }
  if (!cardValidator.expirationMonth(monthStr).isValid) {
    return {
      path: paths.month,
      message: "Enter a month from 1 to 12.",
    };
  }
  if (!cardValidator.expirationYear(yearStr, elapsed).isValid) {
    const y = new Date().getFullYear();
    return {
      path: paths.year,
      message: `Enter a year from ${y} to ${MAX_EXPIRATION_CALENDAR_YEAR}.`,
    };
  }
  return {
    path: paths.month,
    message: "This card has expired.",
  };
}

export function fullNameToBillingName(fullName: string): {
  first: string;
  last: string;
} {
  const trimmed = fullName.trim();
  if (!trimmed) {
    return { first: "", last: "" };
  }
  const spaceIndex = trimmed.indexOf(" ");
  if (spaceIndex === -1) {
    return { first: trimmed, last: trimmed };
  }
  return {
    first: trimmed.slice(0, spaceIndex),
    last: trimmed.slice(spaceIndex + 1).trim(),
  };
}

export function getCvvSize(cardNumber: string): number {
  const result = cardValidator.number(cardNumber);
  const sizes = result.card?.code.size;
  if (sizes === undefined) return 4;
  return Array.isArray(sizes) ? (sizes[0] ?? 4) : sizes;
}

export function validateCardNumberAndCVV(
  cardNumber: string,
  cvv: string,
): PaymentMethodFieldError | null {
  const numberResult = cardValidator.number(cardNumber);
  if (!numberResult.isValid) {
    return {
      path: ["cardNumber"],
      message: "Enter a valid card number.",
    };
  }

  const cvvLengths =
    numberResult.card !== null ? numberResult.card.code.size : [3, 4];
  const cvvResult = cardValidator.cvv(cvv.trim(), cvvLengths);
  if (!cvvResult.isValid) {
    const isAmex = numberResult.card?.type === "american-express";
    return {
      path: ["cvv"],
      message: isAmex
        ? "Enter the 4-digit CID from the front of your card."
        : "Enter the security code from your card (usually 3 digits).",
    };
  }

  return null;
}
